import { useEffect, useMemo } from "react";
import { CancelledError } from "@tanstack/react-query";
import Bugsnag from "~/lib/Bugsnag";

function isReportableError(error: unknown): error is Error {
  return (
    error instanceof Error && error.name !== "AbortError" && !(error instanceof CancelledError)
  );
}

/**
 * Reports a query error to Bugsnag, filtering out AbortErrors and React Query
 * cancellations which are expected during unmount/remount cycles.
 */
export function useReportQueryError(
  isError: boolean,
  error: Error | null,
  context: string,
  metadata: Record<string, unknown>,
): void {
  // Stabilize metadata to avoid re-firing the effect on every render.
  const stableMetadata = useMemo(
    () => metadata,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(metadata)],
  );

  useEffect(() => {
    if (isError && isReportableError(error)) {
      Bugsnag.notify(error, (event) => {
        event.context = context;
        event.addMetadata("drawer", stableMetadata);
      });
    }
  }, [isError, error, context, stableMetadata]);
}
